import {
  ChevronDownIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { CheckIcon } from '@langchain/untitled-ui-icons';
import { Autocomplete } from '@mui/joy';

import { useEffect, useMemo, useState } from 'react';

import {
  Command,
  CommandGroup,
  CommandItem,
  CommandList,
} from '@/components/Command';
import { CustomValue } from '@/components/FilterBar/components/FilterBarArgumentInput';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/Popover';
import { PopoverDropdownMenu } from '@/components/PopoverDropdownMenu';
import { TextOverflowTooltip } from '@/components/TextOverflowTooltip';
import { Button, IconButton, Input, Text } from '@/design-system';
import MetadataIcon from '@/icons/MetadataIcon.svg?react';
import { cn } from '@/utils/tailwind';
import { tryParseJSON } from '@/utils/try-parse-json';

import { ExampleFilter } from '../types/ExamplesSearchModel';

export function ExampleFilterBarOperands(props: {
  commonMetadata: Record<string, unknown[]>;
  currentFilters: ExampleFilter[];
  onChange: (operand: ExampleFilter[]) => void;
}) {
  return (
    <div className="flex w-full flex-col items-center gap-2">
      {props.currentFilters.map((filter, index) => {
        return (
          <div className="flex w-full items-center gap-2" key={index}>
            <Text color="tertiary">{index > 0 ? 'And' : 'Where'}</Text>
            <ExampleFilterBarOperand
              commonMetadata={props.commonMetadata}
              key={index}
              filter={filter}
              onChange={(newFilter) => {
                if (newFilter === null) {
                  const newFilters = [...props.currentFilters];
                  newFilters.splice(index, 1);
                  props.onChange(newFilters);
                  return;
                }
                const newFilters = [...props.currentFilters];
                newFilters[index] = newFilter;
                props.onChange(newFilters);
              }}
            />
          </div>
        );
      })}
    </div>
  );
}

const FILTER_OPTIONS = ['metadata', 'full_text'];
const FILTER_OPTION_TO_LABEL: Record<string, string> = {
  metadata: 'Metadata',
  full_text: 'Full Text',
};
const FILTER_OPTION_TO_ICON = {
  metadata: MetadataIcon,
  full_text: MagnifyingGlassIcon,
};
const FILTER_OPTION_TO_ICON_INSTANCE = {
  metadata: <MetadataIcon className="h-4 w-4" />,
  full_text: <MagnifyingGlassIcon className="h-4 w-4" />,
};

function ExampleFilterBarOperand(props: {
  commonMetadata: Record<string, unknown[]>;
  filter: ExampleFilter;
  onChange: (filter: ExampleFilter | null) => void;
}) {
  return (
    <div className="flex w-full items-center justify-between gap-2">
      <PopoverDropdownMenu
        values={FILTER_OPTIONS}
        getIsSelected={(value) => value === props.filter.type}
        onSelect={(value) => {
          if (value === 'metadata') {
            props.onChange({ type: value, value: {} });
          } else {
            props.onChange({ type: value, value: '' });
          }
        }}
        getTitle={(value) => FILTER_OPTION_TO_LABEL[value]}
        getIcon={(value) => FILTER_OPTION_TO_ICON_INSTANCE[value]}
        asChild
      >
        <Button
          variant="outlined"
          color="secondary"
          rightDecorator={ChevronDownIcon}
          leftDecorator={
            props.filter.type
              ? FILTER_OPTION_TO_ICON[props.filter.type]
              : undefined
          }
          className="shrink-0"
        >
          {props.filter.type
            ? FILTER_OPTION_TO_LABEL[props.filter.type]
            : 'Select'}
        </Button>
      </PopoverDropdownMenu>
      {props.filter.type === 'metadata' ? (
        <ExampleMetadataPicker
          commonMetadata={props.commonMetadata}
          value={props.filter.value as Record<string, string>}
          onChange={(value) => {
            props.onChange({ type: 'metadata', value });
          }}
        />
      ) : props.filter.type === 'full_text' ? (
        <ExampleFullTextInput
          value={props.filter.value as string}
          onChange={(value) => {
            props.onChange({ type: 'full_text', value });
          }}
        />
      ) : null}
      <IconButton
        size="sm"
        color="secondary"
        variant="plain"
        onClick={() => props.onChange(null)}
        icon={XMarkIcon}
        label="Clear filter"
        tooltipProps={{
          side: 'top',
        }}
      />
    </div>
  );
}

function ExampleMetadataPicker(props: {
  commonMetadata: Record<string, unknown[]>;
  value: Record<string, unknown>;
  onChange: (value: Record<string, unknown>) => void;
}) {
  const currentCommonMetadataValues = Object.entries(
    props.commonMetadata
  ).reduce((acc, [, values]) => {
    const currentValues = new Set(values);
    for (const value of values) {
      currentValues.add(value);
    }
    return [...acc];
  }, []);
  currentCommonMetadataValues.sort();
  const currentCommonMetadataKeys = Object.keys(props.commonMetadata);
  currentCommonMetadataKeys.sort();
  const currentCommonMetadataValuesJson = JSON.stringify(
    currentCommonMetadataValues
  );
  const currentCommonMetadataKeysJson = JSON.stringify(
    currentCommonMetadataKeys
  );

  const [commonMetadata, setCommonMetadata] = useState(props.commonMetadata);
  useEffect(() => {
    // accumulate all metadata keys and values
    const currentCommonMetadataState = { ...commonMetadata };
    for (const [key, values] of Object.entries(props.commonMetadata)) {
      if (!Object.keys(currentCommonMetadataState).includes(key)) {
        currentCommonMetadataState[key] = values;
      } else {
        const currentValues = new Set(values);
        for (const value of values) {
          currentValues.add(value);
        }
        currentCommonMetadataState[key] = Array.from(currentValues);
      }
    }
    setCommonMetadata(currentCommonMetadataState);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentCommonMetadataValuesJson, currentCommonMetadataKeysJson]);

  const metadataKey = useMemo(() => Object.keys(props.value)[0], [props.value]);

  const commonMetadataValues = useMemo(() => {
    const metadataValues = Array.from(
      new Set(commonMetadata[metadataKey] ?? [])
    );

    return metadataValues.map((value) => {
      return {
        title: typeof value === 'string' ? value : JSON.stringify(value),
        value,
      };
    });
  }, [commonMetadata, metadataKey]);
  const commonMetadataKeys = Object.keys(commonMetadata);

  const selectedValue = useMemo(() => {
    if (!metadataKey) return undefined;

    const currentValue = props.value[metadataKey];

    const existingValue = commonMetadataValues.find(
      (value) => value.value === currentValue
    );
    if (existingValue) return existingValue;

    const title =
      currentValue === undefined
        ? ''
        : typeof currentValue === 'string'
        ? currentValue
        : JSON.stringify(currentValue);

    return {
      title,
      value: currentValue ?? '',
    };
  }, [metadataKey, props.value, commonMetadataValues]);

  return (
    <div className="line-clamp-1 flex items-center gap-2">
      <Autocomplete
        disableClearable
        freeSolo
        placeholder="Key..."
        options={commonMetadataKeys}
        value={metadataKey ?? ''}
        sx={{
          width: 130,
          fontSize: '12px',
          height: '32px',
          minHeight: '32px',
          backgroundColor: 'transparent',
        }}
        onInputChange={(_, newValue) => {
          props.onChange({ [newValue]: Object.values(props.value)[0] });
        }}
        renderOption={(props, option, state) => {
          const { selected } = state;
          return (
            <li {...props}>
              <div
                className={cn(
                  'cursor-pointer px-4 py-2 text-sm hover:bg-tertiary active:bg-tertiary',
                  selected &&
                    'bg-[#DDF1FF] text-[#044DA7] hover:bg-[#DDF1FF] hover:text-[#044DA7] dark:bg-[#02153C] dark:text-[#ADDBFF] dark:hover:bg-[#02153C] dark:hover:text-[#ADDBFF]'
                )}
              >
                <div className="line-clamp-1">{option}</div>
              </div>
            </li>
          );
        }}
        slotProps={{
          listbox: {
            sx: {
              wordBreak: 'break-all',
              minWidth: '200px',
              maxWidth: '200px',
            },
          },
        }}
      />
      <Text>is</Text>
      <SingleMetadataInput
        value={selectedValue}
        onChange={(value) => {
          props.onChange({ [metadataKey]: value.value });
        }}
        suggestedValues={commonMetadataValues}
      />
    </div>
  );
}

function ExampleFullTextInput(props: {
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <Input
      type="text"
      size="sm"
      value={props.value}
      onChange={(value) => props.onChange(value)}
      className="w-full"
      placeholder="contains..."
    />
  );
}

type ValueType = {
  title: string;
  value: unknown;
};

function SingleMetadataInput(props: {
  value: ValueType | undefined;
  onChange: (value: ValueType) => void;
  suggestedValues: ValueType[];
}) {
  const [open, setOpen] = useState(false);
  const [state, setState] = useState('');
  const filteredValues = props.suggestedValues.filter((value) =>
    value.title.toLowerCase().includes(state.toLowerCase())
  );
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        className={cn(
          'flex grow items-center gap-1.5 whitespace-nowrap rounded-md border border-secondary bg-transparent px-2 py-2',
          { 'text-tertiary': !(props.value && props.value.title !== '') }
        )}
      >
        <Text variant="sm">
          {props.value && props.value.title !== ''
            ? `${props.value?.title.slice(0, 30)}${
                (props.value?.title.length ?? 0) > 30 ? '...' : ''
              }`
            : 'Value'}
        </Text>
      </PopoverTrigger>
      <PopoverContent className="max-h-[500px] overflow-auto p-0" align="start">
        <Command shouldFilter={false}>
          <Input
            placeholder="Value..."
            className="m-2 max-w-[calc(100%-16px)]"
            value={state ?? ''}
            onChange={(value) => setState(value)}
          />

          {!state && props.suggestedValues.length === 0 ? (
            <Text className="w-full py-7 pb-8 text-center">Enter a value</Text>
          ) : null}

          <CommandList>
            {filteredValues.length > 0 && (
              <CommandGroup
                className="m-0 [&_[cmdk-group-heading]]:bg-tertiary [&_[cmdk-group-items]]:m-2"
                heading={
                  <span className="px-2 text-xs font-medium uppercase tracking-wide text-tertiary">
                    Top values
                  </span>
                }
              >
                {filteredValues.map((value, idx) => (
                  <CommandItem
                    key={idx}
                    className="flex items-center justify-between gap-2"
                    onSelect={() => props.onChange(value)}
                  >
                    <TextOverflowTooltip>
                      {typeof value.value === 'string'
                        ? value.value
                        : `${value.value}`}
                    </TextOverflowTooltip>
                    {value.value === props.value ? (
                      <CheckIcon className="h-4 w-4 flex-shrink-0" />
                    ) : null}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
            {state && !filteredValues.some((value) => value.title === state) ? (
              <CommandGroup
                className="m-0 [&_[cmdk-group-heading]]:bg-tertiary [&_[cmdk-group-items]]:m-2"
                heading={
                  <Text color="tertiary" variant="sm">
                    Custom value
                  </Text>
                }
              >
                <CustomValue
                  key={state}
                  value={state}
                  onChange={(value) => {
                    props.onChange({
                      title: tryParseJSON(value, true),
                      value: value,
                    });
                  }}
                />
              </CommandGroup>
            ) : null}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
